import type { FC, ReactNode } from 'react';

import { useContent } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import { EnecoLogo, VisuallyHidden } from '@eneco/sparky';

import type { LogoContent } from './logo-content.types';

/**
 * `Logo` block for the CMS-agnostic lane, serving **both** providers.
 *
 * The logo mark is a hardcoded brand SVG, not CMS content — matching production exactly
 * (`libs/sitecore/components/eneco/src/ImageLink/ImageLink.tsx`, registered as the `Logo` rendering in
 * every Eneco-branded `sitecoreComponents.ts`): it too renders `<EnecoLogo />` unconditionally and reads
 * the CMS only for the click-through `link` and its accessible text. `siteWideImageLink.image` — the
 * field this block used to (wrongly) treat as the logo's source — is unused by production and, on real
 * content, has no asset behind it either; neither is a bug to chase.
 *
 * The link sits on the block itself on Sitecore (`ImageLink`'s `fields.link`) and behind `siteWideContent`
 * on Sanity; both are read.
 *
 * One brand per component, as legacy has one `ImageLink.tsx` per brand package
 * (`libs/sitecore/components/{eneco,oxxio,woonenergie}`), rather than a switch at render time: this is the
 * Eneco one, and a container of another brand registers its own as `Logo` (`LogoOxxio.tsx`).
 */
const Logo: FC = () => <BrandLogo mark={<EnecoLogo />} fallbackLabel="Eneco" />;

/** A brand's mark, linked as legacy's `ImageLink` links it, with the link's text as its hidden label. */
export const BrandLogo: FC<{ mark: ReactNode; fallbackLabel?: string }> = ({ mark, fallbackLabel }) => {
  const content = useContent<LogoContent>();
  const link = linkWithUrl(content.link) ?? linkWithUrl(content.siteWideContent?.link);

  const logo = (
    <>
      <VisuallyHidden>{link?.text ?? fallbackLabel}</VisuallyHidden>
      {mark}
    </>
  );

  if (!link) return logo;

  return (
    // Sparky's own link components (TextLink/NavLink) default rel to 'noreferrer' whenever
    // target is '_blank', guarding against reverse tabnabbing; this is a bare <a>, so the same
    // default is applied explicitly. `link.rel` (set with target: '_blank' when a link is converted) wins
    // when present; the fallback only matters for a link shape that sets target without it.
    <a href={link.href} target={link.target} rel={link.rel ?? (link.target === '_blank' ? 'noreferrer' : undefined)}>
      {logo}
    </a>
  );
};

export default Logo;
